import { type MutateFunctionRest, type MutateOptions, type UseMutationResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AxiosError, isAxiosError } from "axios";
import type { DateTime } from "luxon";
import type React from "react";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type Department, useAuth } from "../../../../contexts/AuthProvider";
import { type AnalyticValues, useAnalytics } from "../../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { DEPARTMENT_FACILITY_QUERY_KEY } from "../../../../hooks/useDepartmentFacility";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { useRunWageBreakdown } from "../../../../hooks/useRunWageBreakdown";
import type { TranslationKeys } from "../../../../i18n/types";
import { backendService } from "../../../../services/backend.service";
import { agencySourceLabel } from "../../../../utils/agencyIntegrations";
import { trackErrorEvent, trackEvent } from "../../../../utils/analytics";
import { withPreservedTrainingOrientationWage, withPreservedWageRulesConfig } from "../../../../utils/DepartmentWage.config";
import { errorText } from "../../../../utils/errors";
import type {
	AgencyDeactivationImpact,
	AgencyIntegrationToggleResult,
	AutoApproveRule,
	BreakRulesConfig,
	DepartmentLocation,
	FindDuplicateLocationsBody,
	FindDuplicateLocationsResult,
	HrisIntegrationType,
	NursaWebhookSubscriptionResult,
	NurseFullRole,
	RecalculateCensusResponse,
	RenameDepartmentLocationBody,
	RenameDepartmentLocationResult,
	SetLocationEmrBody,
	SetLocationEmrResult,
	UpdateSingleDepartmentLocationBody,
	UpdateSingleDepartmentLocationResult,
} from "../../../../utils/models";
import { type AgencySource, EmploymentType, RateHoursBonusType } from "../../../../utils/models";
import { locationComparator } from "../../../../utils/utilFunctions";
import {
	transformBreakRulesConfigsForBackend,
	transformHoursRateConfigsForBackend,
	transformOvertimeConfigsForBackend,
} from "../../../../utils/wageRules/forBackend";
import { credentialRowIdsFromError } from "../../../Locations/delete/credentialRowIdsFromError";
import { duplicateOfIdsFromError } from "../../../Locations/duplicates/duplicateOfIdsFromError";
import type { InlineEditField } from "../../../Locations/inlineEdit/inlineRowPatch";
import { JobRemovalConflictError, type JobRemovalConflicts, parseJobRemovalConflict } from "../jobRemovalConflicts";
import { DEFAULT_ADMIN_FORM_ROW, getFormDataFromDept, isSameScheduleVisibilityDate, toFloatHours, transformJobShiftTimesForBackend } from "../utils";
import { useAdminUsers, useAvailableAdminUsers, useUpdateAdminUsers } from "./adminUsersHooks";
import { AutoApproveRuleValidationError, buildDeptConfigUpdatePayload, transformAutoApproveRulesForBackend } from "./deptConfigPayload";
import { facilityChoiceForCreate } from "./facilityChoice";
import { HOSPITAL_FACILITIES_QUERY_KEY } from "./facilityHooks";
import { deriveTimezoneLocked } from "./generalMetadataConsts";
import { JobPpdFlushError, type PendingJobPpdOverrides, pendingJobPpdAfterFlush, useFlushJobPpdOverrides } from "./jobPpdOverrides";
import { schedulePushDepartmentConfigKey } from "./schedulePush/schedulePushHooks";
import type { AdminUser, GeneralFormData, SubmitDeptConfigResult } from "./types";
import { getEmrIntegrationsReasons, getMissingGeneralFieldReasons } from "./validations";

/**
 * The `UIVisibleError` message KEY behind a failed request, when there is one.
 *
 * Returned as a key rather than a rendered sentence so a caller can decide where it belongs — a 409
 * such as the timezone lock needs to appear inside the Airtable wizard, not only in the page's snackbar.
 *
 * Local rather than shared: this shape is parsed in ~5 places across the app already
 * (`AuthProvider`, `AddDayOffDialog`, `CreateSftpFolderDialog`, …), and unifying them is a refactor
 * of call sites this change has no business touching.
 */
function uiVisibleErrorKey(err: unknown): string | undefined {
	const cause = (err as { cause?: unknown })?.cause ?? err;
	const detail = (cause as AxiosError<{ detail?: { message?: string; error_type?: string } | string }>)?.response?.data?.detail;
	return detail && typeof detail === "object" && detail.error_type === "UIVisibleError" ? detail.message : undefined;
}

/**
 * @param onPpdOverridesFlushed Called once per save that actually wrote a PPD override, so the caller
 * can recalculate past wage data a single time (not once per toggle the operator flipped).
 */
export function useUpdateGeneralForm(generalFormData: GeneralFormData, onPpdOverridesFlushed?: () => void) {
	const { userInfo } = useAuth();
	const { chosenDepartment } = useHospitalData();
	const { department_id, staffScheduleDisplayUntilDate: publishedScheduleVisibilityDate } = chosenDepartment;
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	// Jobs that already have a persisted row, so `transformJobShiftTimesForBackend` can keep sending
	// an explicitly-cleared row (which suppresses the `default` fallback) rather than dropping it.
	const persistedJobShiftTimeIds = useMemo(() => new Set((chosenDepartment.jobShiftTimes ?? []).map((row) => row.jobId)), [chosenDepartment.jobShiftTimes]);
	const flushJobPpdOverrides = useFlushJobPpdOverrides(department_id);
	// The PPD keys the running save read. A toggle made WHILE it is in flight is not among them, so
	// the success reset can spend exactly what was sent instead of wiping the buffer.
	const flushedJobPpdIds = useRef<readonly string[]>([]);
	const spendFlushedJobPpd = useCallback((pending: PendingJobPpdOverrides) => pendingJobPpdAfterFlush(pending, flushedJobPpdIds.current), []);
	const queryClient = useQueryClient();

	const mutation = useMutation<GeneralFormData, Error>({
		mutationFn: async () => {
			if (!userInfo.support) {
				throw Error("Current user is not signed in as a support user");
			}

			let response: Awaited<ReturnType<typeof backendService.updateGeneralForm>>;
			try {
				response = await backendService.updateGeneralForm(department_id, {
					config: buildDeptConfigUpdatePayload(generalFormData, { publishedScheduleVisibilityDate, jobManagementEnabled, persistedJobShiftTimeIds }),
				});
				if (response.status >= 400) {
					throw Error(response.statusText);
				}
			} catch (e) {
				if (e instanceof AutoApproveRuleValidationError) throw e;
				// DAT-464: a de-enabled job that is still in use comes back as a 409 carrying the list of
				// blockers. Rethrow it typed — the generic wrap below would flatten it into a snackbar
				// string and lose exactly the detail Ops needs to act on.
				const jobConflicts = parseJobRemovalConflict(e);
				if (jobConflicts) {
					throw new JobRemovalConflictError(jobConflicts);
				}
				throw Error("Could not update department configuration", { cause: e });
			}

			// Deliberately outside the config try: the flush is unreachable when the PUT throws, which is
			// what keeps a 409 from the job-removal guard from touching PPD, and its own failure must not
			// be re-wrapped as a config-save error.
			const pendingJobPpd = generalFormData.jobPpdOverrides;
			flushedJobPpdIds.current = Object.keys(pendingJobPpd);
			try {
				if (await flushJobPpdOverrides(pendingJobPpd)) onPpdOverridesFlushed?.();
			} catch (error) {
				// A partial flush still changed PPD inclusion for the writes that landed, and PPD inclusion
				// is what the wage breakdown is computed from — so the recalculation still runs even though
				// this save is about to be reported as failed.
				if (error instanceof JobPpdFlushError && error.landed > 0) onPpdOverridesFlushed?.();
				throw error;
			}

			return response.data;
		},
		// The facility list carries each building's inactivity windows, and a new department of it starts at
		// them; a list cached before this save would start one at the old values and save those over the building.
		onSuccess: () => queryClient.invalidateQueries({ queryKey: HOSPITAL_FACILITIES_QUERY_KEY }),
		// The schedule-push panel reads the HRIS rows this save writes (whether a HostedTime shifts integration is
		// active, the facility code it prefills). Settled rather than success: a save that wrote the config and then
		// failed on the PPD flush still changed those rows.
		onSettled: () => queryClient.invalidateQueries({ queryKey: schedulePushDepartmentConfigKey(department_id) }),
		throwOnError: false,
	});

	return { ...mutation, spendFlushedJobPpd };
}

export function useRecalculateCensus() {
	const {
		chosenDepartment: { department_id },
		showErrorSnackbar,
	} = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();

	return useMutation<
		RecalculateCensusResponse,
		AxiosError<{ detail?: { message?: string; error_type?: string } | string }>,
		{ startDate: DateTime; endDate: DateTime }
	>({
		mutationFn: async ({ startDate, endDate }) => {
			const response = await backendService.recalculateCensus({ departmentId: department_id, startDate, endDate });
			if (response.status >= 400) {
				throw new Error(response.statusText);
			}
			return response.data;
		},
		onSuccess: (data, { startDate, endDate }) => {
			trackEvent("Department configuration - recalculate census", {
				department_id,
				start_date: startDate.toFormat("yyyy-MM-dd"),
				end_date: endDate.toFormat("yyyy-MM-dd"),
				days: Math.round(endDate.startOf("day").diff(startDate.startOf("day"), "days").days) + 1,
				already_pending: data.alreadyPending,
			});
			// A duplicate request is not an error, but saying "started" would wrongly imply a second run.
			showSnackbar(
				t(
					data.alreadyPending
						? "department.config.integrations.emr.recalculateCensus.alreadyPending"
						: "department.config.integrations.emr.recalculateCensus.success",
				),
				"success",
			);
		},
		onError: (error) => {
			// Same UIVisibleError unwrapping as AddDayOffDialog/hooks.ts — the backend sends the i18n
			// key in `detail.message`, and FastAPI serializes the dict detail as a string on some paths.
			const detail = error.response?.data?.detail;
			let errorMessage = t("department.config.integrations.emr.recalculateCensus.error");
			if (typeof detail === "object" && detail?.error_type === "UIVisibleError" && detail?.message) {
				errorMessage = t(detail.message);
			} else if (typeof detail === "string") {
				try {
					const parsed = JSON.parse(detail);
					if (parsed?.error_type === "UIVisibleError" && parsed?.message) {
						errorMessage = t(parsed.message);
					}
				} catch {
					errorMessage = detail.length < 200 ? detail : errorMessage;
				}
			}
			trackErrorEvent("Department configuration - recalculate census failed", { department_id, error: errorMessage });
			showErrorSnackbar(errorMessage, {}, error);
		},
		throwOnError: false,
	});
}

export function useCreateDepartment({
	newDepartment,
	newUsers,
	newDepartmentLocations,
	setIsNewDept,
}: {
	newDepartment: GeneralFormData;
	newUsers: AdminUser[];
	newDepartmentLocations: DepartmentLocation[];
	setIsNewDept?: Dispatch<SetStateAction<boolean>>;
}) {
	const { userInfo } = useAuth();
	const queryClient = useQueryClient();

	return useMutation<
		{
			config: Department;
			newUsers: AdminUser[];
			newDepartmentLocations: DepartmentLocation[];
		},
		Error
	>({
		mutationFn: async () => {
			if (!userInfo.support) {
				throw Error("Current user is not signed in as a support user");
			}

			try {
				// jobShiftTimes is a job-id-keyed map on the form but an array on the wire, so it has to
				// leave the spread and be rebuilt below — otherwise Pydantic rejects the whole request.
				// A new department always renders job rows (`jobMode = isNewDept || …`), so this is
				// reachable on the very first save.
				// jobPpdOverrides leaves the spread too: it is not part of the config payload, and a
				// department with no id yet has nothing to hang an override on. facility is sent beside
				// the config, not in it.
				const { departmentType, staffScheduleDisplayUntilDate, jobShiftTimes, jobPpdOverrides: _jobPpdOverrides, facility, ...deptConfig } = newDepartment;
				if (!departmentType) {
					throw new Error("Department type is required");
				}
				const response = await backendService.createDepartment({
					config: {
						...deptConfig,
						// Trimmed like the facility picker's lookup, so " Sunrise " is saved as the same network as "Sunrise".
						hospital: deptConfig.hospital.trim(),
						type: departmentType,
						staffScheduleDisplayUntilDate: staffScheduleDisplayUntilDate ? staffScheduleDisplayUntilDate.toFormat("yyyy-MM-dd") : null,
						week1FirstSunday: newDepartment.week1FirstSunday.toFormat("yyyy-MM-dd"),
						hoursRateConfig: transformHoursRateConfigsForBackend(newDepartment.hoursRateConfig, newDepartment.roles, newDepartment.jobIds),
						overtimeConfig: transformOvertimeConfigsForBackend(newDepartment.overtimeConfig, newDepartment.roles, newDepartment.jobIds),
						breakRulesConfig: transformBreakRulesConfigsForBackend(newDepartment.breakRulesConfig, newDepartment.roles, newDepartment.jobIds),
						autoApproveRules: transformAutoApproveRulesForBackend(newDepartment.autoApproveRules),
						// No persisted rows exist yet on a create, so nothing needs preserving as an
						// explicitly-cleared row — hence the empty persisted-id set.
						jobShiftTimes: transformJobShiftTimesForBackend(jobShiftTimes, newDepartment.jobIds, new Set()),
					},
					facility: facilityChoiceForCreate(facility),
					newUsers,
					newDepartmentLocations,
				});
				if (response.status >= 400) {
					throw Error(response.statusText);
				}

				return response.data;
			} catch (e) {
				if (e instanceof AutoApproveRuleValidationError) throw e;
				// If it's a validation error from transformHoursRateConfigsForBackend, preserve the message
				if (e instanceof Error && (e.message.includes("At least one role must be selected") || e.message.includes("Invalid time ranges"))) {
					throw e;
				}
				throw Error("Could not create the requested department", { cause: e });
			}
		},
		onSuccess: async (data) => {
			if (data) {
				trackEvent("Department configuration - department successfully created", {
					department_id: data.config.department_id,
					generalFormData: data.config,
					newUsers: data.newUsers,
					newDepartmentLocations: data.newDepartmentLocations,
				});
				setIsNewDept?.(false);
			}
			// The create may have made a facility, which the next department of that building must be able to pick.
			await queryClient.invalidateQueries({ queryKey: HOSPITAL_FACILITIES_QUERY_KEY });
			// Joining a facility adds a department its other departments' settings must now name.
			await queryClient.invalidateQueries({ queryKey: DEPARTMENT_FACILITY_QUERY_KEY });
		},
	});
}

/**
 * The Nursa facilities our account can post to, for the department's Facility ID picker.
 *
 * Returns the WHOLE response rather than just the array — `status` is what lets the caller explain an
 * empty list ("Nursa is down" vs "our account has no facilities" vs "not configured here"), which a
 * bare `NursaFacility[]` cannot. Same reasoning, and same shape, as `useSftpDepartmentKeys`.
 *
 * `enabled` carries the caller's support check AND its lazy-open gate: the endpoint is support-only
 * and it calls out to Nursa, so an admin without support would otherwise fire one guaranteed-403
 * request on every visit to the Integrations tab. `retry` is off for the same reason — a 403, missing
 * credentials or a Nursa outage is a settled answer, not a blip worth three tries.
 *
 * Deliberately no `onError` snackbar: the picker surfaces the failure as an inline hint next to the
 * field, and a toast on every Integrations-tab visit during a Nursa outage would be pure noise.
 */
export function useNursaFacilities({ enabled }: { enabled: boolean }) {
	const { chosenDepartment } = useHospitalData();

	return useQuery({
		queryKey: ["nursa-facilities", chosenDepartment.department_id],
		queryFn: async () => {
			const response = await backendService.getNursaFacilities(chosenDepartment.department_id);
			return response.data;
		},
		enabled,
		retry: false,
		staleTime: 60_000,
	});
}

/**
 * Whether this department's Nursa integration is actually live — connection and webhook both.
 *
 * Costs two Nursa round trips, so it is gated on the caller (support, Nursa section mounted) rather
 * than fired from the tab shell. `retry: false` for the roster's reason: a Nursa outage is a settled
 * answer, and the answer it produces — "unknown" — is one the UI renders honestly rather than
 * retrying behind the operator's back.
 */
export function useNursaReadiness({ enabled }: { enabled: boolean }) {
	const { chosenDepartment } = useHospitalData();

	return useQuery({
		queryKey: ["nursa-readiness", chosenDepartment.department_id],
		queryFn: async () => {
			const response = await backendService.getNursaReadiness(chosenDepartment.department_id);
			return response.data;
		},
		enabled,
		retry: false,
		staleTime: 60_000,
	});
}

/** Shorter terms are never sent: Nursa answers a missing `name` with HTTP 500, not an empty list. */
export const NURSA_FACILITY_SEARCH_MIN_TERM_LENGTH = 2;

/**
 * One page of Nursa's facility DIRECTORY, searched by name.
 *
 * The counterpart to `useNursaFacilities`, and the distinction is the feature: that one lists the
 * facilities we already have a connection to — three, in production — so the building being
 * onboarded is never in it. This one reaches every facility Nursa has.
 *
 * The term is in the query key, so each distinct search caches on its own and backtracking a
 * character is free. `staleTime` is long because a national facility directory does not churn
 * within a configuration session, and `retry: false` matches the roster's reasoning: a Nursa outage
 * is a settled answer, and the field stays typeable either way.
 *
 * `enabled` carries the minimum length rather than leaving it to the caller — gating the request is
 * a correctness requirement here, not a debounce nicety.
 */
export function useSearchNursaFacilities(term: string, { enabled }: { enabled: boolean }) {
	const { chosenDepartment } = useHospitalData();
	const trimmed = term.trim();

	return useQuery({
		queryKey: ["nursa-facility-search", chosenDepartment.department_id, trimmed],
		queryFn: async () => {
			const response = await backendService.searchNursaFacilities(chosenDepartment.department_id, trimmed, 1);
			return response.data;
		},
		enabled: enabled && trimmed.length >= NURSA_FACILITY_SEARCH_MIN_TERM_LENGTH,
		retry: false,
		staleTime: 5 * 60 * 1000,
	});
}

/**
 * Adds the department's saved Nursa Facility ID to our Nursa webhook registration.
 *
 * Nursa only delivers shift events for facilities listed on that registration, so a department
 * mapped to a facility nobody subscribed hears nothing back — outbound posts work, inbound
 * `shift.created` / `shift.scheduled` / `shift.cancelled` never arrive. Offered right after a save
 * that introduced a new facility id, which is the moment that gap is created.
 *
 * The facility id is not sent: the backend reads the persisted one off the department, so only a
 * saved id can reach Nursa's registration.
 */
export function useRegisterNursaFacilityWebhook() {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();

	return useMutation<NursaWebhookSubscriptionResult, AxiosError, void>({
		mutationFn: async () => {
			const response = await backendService.registerNursaFacilityWebhook(chosenDepartment.department_id);
			return response.data;
		},
		onSuccess: (data) => {
			showSnackbar(
				t(data.status === "added" ? "department.config.nursa.webhook.added" : "department.config.nursa.webhook.alreadySubscribed", {
					facilityId: data.facility_id,
				}),
				"success",
			);
			trackEvent("Department configuration - nursa webhook facility subscribed", {
				department_id: chosenDepartment.department_id,
				facility_id: data.facility_id,
				status: data.status,
				subscribed_facilities: data.subscribed_facility_count,
			});
		},
		// The message is a fallback only: showErrorSnackbar prefers the backend's translated
		// UIVisibleError key, which says which half failed (read vs. update).
		onError: (error) =>
			showErrorSnackbar(t("error.nursa.webhook.update.failed"), { action: "nursa_webhook_subscription", department_id: chosenDepartment.department_id }, error),
		throwOnError: false,
	});
}

/**
 * Un-subscribes a facility the department has stopped using, so Nursa stops sending events that no
 * longer resolve to anyone. The inverse of `useRegisterNursaFacilityWebhook`, called when a save
 * replaces or clears the id — without it the abandoned facility stays on the shared registration
 * forever and its events log as errors on every delivery.
 *
 * Failures are reported but never block: the new id is already saved and subscribed by then, so a
 * stranded old id is untidy, not broken.
 */
export function useUnregisterNursaFacilityWebhook() {
	const { t } = useTranslation();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();

	return useMutation<NursaWebhookSubscriptionResult, AxiosError, string>({
		mutationFn: async (facilityId: string) => {
			const response = await backendService.unregisterNursaFacilityWebhook(chosenDepartment.department_id, facilityId);
			return response.data;
		},
		onSuccess: (data) => {
			trackEvent("Department configuration - nursa webhook facility unsubscribed", {
				department_id: chosenDepartment.department_id,
				facility_id: data.facility_id,
				status: data.status,
				subscribed_facilities: data.subscribed_facility_count,
			});
		},
		onError: (error) =>
			showErrorSnackbar(
				t("error.nursa.webhook.unsubscribe.failed"),
				{ action: "nursa_webhook_unsubscribe", department_id: chosenDepartment.department_id },
				error,
			),
		throwOnError: false,
	});
}

/**
 * What switching one agency integration off would touch (DAT-885). Feeds the deactivation dialog's
 * checklist and the un-post checkbox's visibility.
 *
 * `retry: false` and DISPLAY ONLY. The mutation re-derives its own un-post set server-side, so these
 * counts never gate the decision — a failed preflight degrades the dialog's detail and leaves confirm
 * enabled, because a preflight outage must not become a configuration outage.
 *
 * `staleTime: 0`: the dialog opens on an operator's deliberate decision and the counts are about to be
 * acted on, so a cached figure from five minutes ago is worse than a fresh round trip.
 */
export function useAgencyDeactivationImpact(source: AgencySource, enabled: boolean) {
	const { chosenDepartment } = useHospitalData();

	return useQuery({
		queryKey: ["agency-deactivation-impact", chosenDepartment.department_id, source],
		queryFn: async () => {
			const response = await backendService.getAgencyDeactivationImpact(chosenDepartment.department_id, source);
			return response.data;
		},
		enabled: enabled && !!chosenDepartment.department_id,
		retry: false,
		staleTime: 0,
		gcTime: 0,
	});
}

export type AgencyIntegrationToggleVariables = {
	source: AgencySource;
	active: boolean;
	/** Only read by the server when switching OFF; always sent explicitly so no default has to apply. */
	unpostOpenShifts: boolean;
	/** The preflight the operator actually saw, for the analytics event. Absent when it failed to load. */
	impact?: AgencyDeactivationImpact;
};

/**
 * Switches one department's agency integration on or off (DAT-885).
 *
 * Refreshes through `updateUserInfo()` → `setChosenDepartment`, NOT `refreshDepartmentData`: the
 * latter refetches `/department_data` and patches only `staffScheduleDisplayUntilDate`, so it can
 * never see `agencyIntegrations` and the switch would show stale state until a page reload. Same
 * pattern as the end of the department-config save.
 *
 * No optimistic flip. The transition makes real vendor calls; showing it as done before the server
 * agrees is how an operator walks away from a half-applied change.
 */
export function useSetAgencyIntegrationActive() {
	const { t } = useTranslation();
	const { chosenDepartment, setChosenDepartment, showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { updateUserInfo } = useAuth();
	const departmentId = chosenDepartment.department_id;

	return useMutation<AgencyIntegrationToggleResult, AxiosError, AgencyIntegrationToggleVariables>({
		mutationFn: async ({ source, active, unpostOpenShifts }) => {
			const response = await backendService.setAgencyIntegrationActive(departmentId, source, { active, unpostOpenShifts });
			return response.data;
		},
		onSuccess: async (result, variables) => {
			const agency = agencySourceLabel(variables.source);
			// Branch on the discriminator, never on which optional fields happen to be present.
			if (result.transition === "deactivated") {
				showSnackbar(t("department.config.integrations.agency.toggle.deactivated", { agency, count: result.unposted }), "success");
				// `integration` is NULLABLE on this arm only, and null is a success: someone unmapped the
				// facility after the un-post had already reached the vendor, so the backend committed the
				// retractions rather than 409-ing and rolling our `status=deleted` rows back to `posted`
				// (review H3). Nothing above or below dereferences it — every string here is built from
				// `variables.source` and the analytics payload from `variables` — so the only thing owed is
				// telling the operator, because the switch they just turned off is about to render as
				// "Not connected" and that would otherwise look like the change failed.
				if (result.integration === null) showSnackbar(t("department.config.integrations.agency.toggle.deactivatedUnmapped", { agency }), "warning");
				if (result.unpostTruncated) showSnackbar(t("department.config.integrations.agency.toggle.unpostTruncated", { agency }), "warning");
				// A vendor that refused a retraction leaves a live posting nobody can see from here.
				for (const failure of result.failures) {
					showSnackbar(t("department.config.integrations.agency.toggle.unpostFailed", { agency: failure.agency }), "warning");
				}
			} else if (result.webhookError) {
				// Half-on, and recoverable: `active` is true, only the webhook subscribe failed.
				showSnackbar(t("department.config.integrations.agency.toggle.activatedWebhookFailed", { agency }), "warning");
			} else {
				showSnackbar(t("department.config.integrations.agency.toggle.activated", { agency }), "success");
			}

			trackEvent(variables.active ? "Department configuration - agency integration activated" : "Department configuration - agency integration deactivated", {
				department_id: departmentId,
				agency: variables.source,
				unpost_open_shifts: variables.unpostOpenShifts,
				impact_open_shifts: variables.impact?.openShiftsPosted ?? null,
				impact_scheduled_nurses: variables.impact?.scheduledAgencyNurses ?? null,
				impact_pending_responses: variables.impact?.pendingResponses ?? null,
				impact_inbound_events_7d: variables.impact?.inboundEventsRecent ?? null,
			});

			// `/user_info` is the ONLY carrier of `agencyIntegrations`, so this is what makes the switch,
			// the status chip and every downstream posting gate agree with the server without a reload.
			const userInfo = await updateUserInfo();
			const refreshed = userInfo?.departments.find((dept) => dept.department_id === departmentId);
			if (!refreshed) {
				showErrorSnackbar(
					t("department.config.integrations.agency.toggle.refreshFailed", { agency: agencySourceLabel(variables.source) }),
					{ action: "agency_integration_toggle_refresh", department_id: departmentId },
					new Error("Department missing from user info after an agency integration toggle"),
				);
				return;
			}
			setChosenDepartment((prev) => ({ ...prev, agencyIntegrations: refreshed.agencyIntegrations }));
		},
		// The message is a fallback only: showErrorSnackbar prefers the backend's translated
		// UIVisibleError key (`agency.integration.not.mapped` / `agency.integration.toggle.unavailable`).
		//
		// Which is also why those two strings are deliberately AGENCY-NEUTRAL in all three catalogs, even
		// though the backend sends `details: {agency}`: `showErrorSnackbar`
		// (`contexts/HospitalDataProvider.tsx:2231`) resolves a UIVisibleError as a bare
		// `t(parsedDetail.message)` with no interpolation object, so a `{{agency}}` in them would reach
		// the operator as the literal text `{{agency}}`. Teaching that resolver to forward `details` is a
		// shared-primitive change affecting every UIVisibleError in the app, so it is not done here.
		onError: (error, variables) =>
			showErrorSnackbar(
				t("department.config.integrations.agency.toggle.failed", { agency: agencySourceLabel(variables.source) }),
				{ action: "agency_integration_toggle", department_id: departmentId, agency: variables.source },
				error,
			),
		throwOnError: false,
	});
}

/**
 * Whether the chosen department has any shift on the books — the timezone-edit lock's only input.
 *
 * `isNewDept` is a parameter rather than something this hook could read for itself because it is
 * `useDeptConfig`'s local state: while it is true, `chosenDepartment` still points at the
 * PREVIOUSLY selected real department, so querying would answer about the wrong department.
 */
export function useDepartmentHasShifts(isNewDept: boolean) {
	const { chosenDepartment } = useHospitalData();
	return useQuery({
		queryKey: ["departmentHasShifts", chosenDepartment.department_id],
		queryFn: async () => {
			try {
				const response = await backendService.getDepartmentHasShifts(chosenDepartment.department_id);
				return response.data.has_shifts;
			} catch (error) {
				// A failed probe locks the timezone field (see `deriveTimezoneLocked`), so it must never
				// pass silently: without this the operator sees a disabled field and cannot tell
				// "locked because shifts exist" from "we couldn't check". Tracked here and surfaced to
				// the user by the banner `isTimezoneLockUnknown` drives.
				trackErrorEvent("department_has_shifts_probe_error", error, { department_id: chosenDepartment.department_id });
				throw error;
			}
		},
		enabled: !isNewDept && !!chosenDepartment.department_id,
		// Deliberately diverges from the `useGetPccRooms` pattern this hook otherwise mirrors, which sets
		// this false. The answer only ever flips false -> true (a department's first shift), and a cached
		// `false` is the DANGEROUS staleness: it leaves the picker editable on a department that now has
		// shifts. Refetching on focus closes the one case the page cannot otherwise see — this tab sitting
		// on the config page while another tab publishes the department's first shift. Mount already
		// refetches (no global staleTime is set), and a lost race is still refused by the server on save.
		refetchOnWindowFocus: true,
	});
}

export function useDeptConfig({
	onHrisIntegrationSaved,
}: {
	onHrisIntegrationSaved: (integrationType: HrisIntegrationType, savedDepartmentId: string) => void;
}) {
	const { t } = useTranslation();
	const [isNewDept, setIsNewDept] = useState(false);
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment, showErrorSnackbar, refreshDepartmentData, refreshScheduleCache, setChosenDepartment } = useHospitalData();
	const [generalFormData, setGeneralFormDataState] = useState<GeneralFormData>(() => getFormDataFromDept(chosenDepartment));
	const [isDirty, setIsDirty] = useState(false);
	// DAT-464: set when a save is refused because a de-enabled job is still in use. Non-null opens the
	// blocking dialog; the save is already fully rolled back server-side, so there is nothing to undo.
	const [jobRemovalConflicts, setJobRemovalConflicts] = useState<JobRemovalConflicts | null>(null);
	// The Nursa facility id a just-completed save introduced, i.e. the one Nursa is not yet sending
	// us events for. Non-null opens the webhook prompt; the department is already saved either way,
	// so declining costs nothing but the subscription.
	const [pendingNursaWebhookFacilityId, setPendingNursaWebhookFacilityId] = useState<string | null>(null);
	const setGeneralFormData = useCallback<Dispatch<SetStateAction<GeneralFormData>>>((value) => {
		setIsDirty(true);
		setGeneralFormDataState(value);
	}, []);
	// The live PPD buffer, for the async tail of a save: `generalFormData` there is the snapshot from
	// the render that started it, so a toggle made mid-save is invisible to that closure.
	const latestJobPpdOverrides = useRef(generalFormData.jobPpdOverrides);
	useEffect(() => {
		latestJobPpdOverrides.current = generalFormData.jobPpdOverrides;
	}, [generalFormData.jobPpdOverrides]);
	// Only reset the form when the department_id changes or isNewDept toggles —
	// chosenDepartment often gets a new object reference from context updates, which
	// would otherwise wipe unsaved edits and force all tabs to re-render.
	const prevResetKey = useRef<string>(`${chosenDepartment.department_id}|${isNewDept}`);
	useEffect(() => {
		const nextKey = `${chosenDepartment.department_id}|${isNewDept}`;
		if (prevResetKey.current === nextKey) return;
		prevResetKey.current = nextKey;
		// Programmatic (re)load: raw setter avoids marking dirty.
		setGeneralFormDataState(getFormDataFromDept(isNewDept ? undefined : chosenDepartment));
		setIsDirty(false);
	}, [isNewDept, chosenDepartment]);

	// The fail-safe default (unknown ⇒ LOCKED) and the new-department guard live in
	// `deriveTimezoneLocked`, which `deriveTimezoneLocked.spec.ts` pins. Keep the rule there, not
	// inline here — an inline copy is what a "simplify this" edit quietly gets wrong.
	const { data: departmentHasShifts, isError: departmentHasShiftsFailed } = useDepartmentHasShifts(isNewDept);
	const isTimezoneLocked = deriveTimezoneLocked(isNewDept, departmentHasShifts);
	// Locked because the probe FAILED, as opposed to locked because the department really has shifts.
	// Drives the banner that tells the operator which of the two they are looking at.
	const isTimezoneLockUnknown = !isNewDept && departmentHasShiftsFailed;

	const { data: adminUsers } = useAdminUsers();
	const [newUsers, setNewUsers] = useState<AdminUser[]>([]);
	const [existingUsers, setExistingUsers] = useState<AdminUser[]>(adminUsers || []);

	const departmentLocations = chosenDepartment.locations;

	const [existingDepartmentLocations, setExistingDepartmentLocations] = useState<DepartmentLocation[]>(departmentLocations || []);
	const [newDepartmentLocations, setNewDepartmentLocations] = useState<DepartmentLocation[]>([]);
	const [modifiedDepartmentLocations, setModifiedDepartmentLocations] = useState<Set<string>>(new Set());

	const { userInfo, updateUserInfo } = useAuth();

	useEffect(() => {
		if (isNewDept) {
			setExistingUsers([]);
		} else {
			setExistingUsers(adminUsers || []);
		}
	}, [isNewDept, adminUsers]);

	useEffect(() => {
		if (isNewDept) {
			if (!existingUsers.length) {
				setExistingUsers((prev) => {
					if (prev.length) {
						return prev;
					}

					const {
						first_name: firstName,
						username,
						id,
						hide_wage: hideWage,
						phone_number: phoneNumber,
						support,
						is_read_only: isReadonly,
						disable_edit_master_schedule: disableEditMasterSchedule,
						requires_mfa: requiresMFA,
					} = userInfo;
					return [
						{
							...DEFAULT_ADMIN_FORM_ROW,
							adminRoles: [],
							firstName,
							username,
							id,
							hideWage,
							phoneNumber,
							support,
							isReadonly,
							disableEditMasterSchedule,
							requiresMFA,
							rowDisabled: true,
							//todo maybe add BE support to return these fields for userInfo - however its not very interesting since we are disabling this row anyway
							receiveActionRequiredAdminNotifications: false,
							receiveDailyAdminNotifications: false,
							sendDailyActualShiftsCheck: false,
							sendDailyPickupSummary: false,
							receiveDailyExcelReport: false,
							receiveSmsResponseNotifications: false,
							receiveNurseInactivityNotifications: false,
						},
					];
				});
			}
		}
	}, [existingUsers, isNewDept, generalFormData, userInfo, existingDepartmentLocations]);
	useEffect(() => {
		if (!isNewDept) {
			setNewUsers([]);
			setNewDepartmentLocations([]);
		}
	}, [isNewDept]);

	// A save that actually wrote a PPD override changed what counts towards PPD, which makes the
	// department's past wage data stale the moment it lands. Recalculating is the only correct
	// follow-up, so the save runs it itself rather than asking an operator to opt in — once per save,
	// not once per switch they flipped.
	const runWageBreakdown = useRunWageBreakdown();
	const { mutate: startWageBreakdown } = runWageBreakdown;
	const recalculateWagesAfterPpdChange = useCallback(() => startWageBreakdown(), [startWageBreakdown]);
	const {
		mutateAsync: updateGeneralForm,
		isPending: isUpdatingGeneralForm,
		spendFlushedJobPpd,
	} = useUpdateGeneralForm(generalFormData, recalculateWagesAfterPpdChange);
	const { mutateAsync: registerNursaFacilityWebhook, isPending: isRegisteringNursaWebhook } = useRegisterNursaFacilityWebhook();
	const { mutateAsync: unregisterNursaFacilityWebhook } = useUnregisterNursaFacilityWebhook();
	// Read at hook scope on purpose: partway through `submitDeptConfig` a local `userInfo` shadows the
	// one from `useAuth`, so the support flag has to be captured before that shadow begins.
	const isSupportUser = !!userInfo?.support;
	const { mutateAsync: updateAdminUsers, isPending: isUpdatingUsers } = useUpdateAdminUsers({ existingUsers, newUsers });
	const { mutateAsync: updateDepartmentLocations, isPending: isUpdatingDepartmentLocations } = useUpdateDepartmentLocations({
		existingDepartmentLocations: existingDepartmentLocations.filter((row) => modifiedDepartmentLocations.has(row.id)),
		newDepartmentLocations,
	});
	const { data: availableAdminData, isLoading: isAvailableAdminDataLoading } = useAvailableAdminUsers({
		specificDepartmentIds: [chosenDepartment.department_id],
	});

	useEffect(() => {
		if (departmentLocations) {
			setExistingDepartmentLocations(departmentLocations.sort(locationComparator));
			setModifiedDepartmentLocations(new Set());
		}
	}, [departmentLocations]);

	const { mutateAsync: createDepartment, isPending: isCreatingDept } = useCreateDepartment({
		newDepartment: generalFormData,
		newUsers: [],
		newDepartmentLocations,
		setIsNewDept,
	});

	const validateDepartmentLocations = useCallback(() => {
		if (chosenDepartment.masterScheduleV2Enabled) {
			return true;
		}
		// Validate no duplicate records with same section, location and shift type, and not overlapping rules
		const allRecords = [...existingDepartmentLocations, ...newDepartmentLocations];
		const recordMap = new Map<string, DepartmentLocation>();
		// -${record.roles.sort().join(",")

		for (const record of allRecords) {
			const key = `${record.location || ""}-${record.section || ""}-${record.shift_type}`;
			if (recordMap.has(key)) {
				const existingRecord = recordMap.get(key);
				if (
					(existingRecord?.shift_start != record.shift_start || existingRecord?.shift_end != record.shift_end) &&
					existingRecord?.roles.some((role: string) => record.roles.includes(role as NurseFullRole)) &&
					existingRecord?.days.some((day: string) => record.days.includes(day))
				) {
					showErrorSnackbar(`Cannot have overlapping rules with different hours - ${key}`, {}, new Error("Overlapping rules found"));
					return false;
				}
			}
			recordMap.set(key, record);
		}
		return true;
	}, [existingDepartmentLocations, newDepartmentLocations, showErrorSnackbar, chosenDepartment.masterScheduleV2Enabled]);

	const missingGeneralFieldReasons = useMemo<TranslationKeys[]>(() => getMissingGeneralFieldReasons(isNewDept, generalFormData), [isNewDept, generalFormData]);

	const emrIntegrationsReasons = useMemo<TranslationKeys[]>(
		() => getEmrIntegrationsReasons(generalFormData.emrIntegrations ?? []),
		[generalFormData.emrIntegrations],
	);

	// DAT-905: no hours-rate / overtime / break-rules reasons here. Those tables live on Team → Wage
	// Settings, and each section there blocks its own Save. Counting them here would disable this Save
	// over a row that is nowhere on this page to fix.
	const saveDisabledReasons = useMemo<TranslationKeys[]>(
		() => [...missingGeneralFieldReasons, ...emrIntegrationsReasons],
		[missingGeneralFieldReasons, emrIntegrationsReasons],
	);

	const tabErrorCounts = useMemo<Record<string, number>>(
		() => ({
			general: missingGeneralFieldReasons.length,
			integrations: emrIntegrationsReasons.length,
		}),
		[missingGeneralFieldReasons, emrIntegrationsReasons],
	);

	/**
	 * Reports whether a save was actually SUBMITTED, and why not when it was refused with a named reason.
	 *
	 * The result exists because the short-circuits are silent by design: the Save button is already
	 * disabled with a tooltip, so returning early is invisible and correct *for that caller*. Any OTHER
	 * caller — one that assembled a change programmatically and cannot see the tooltip, like the
	 * Airtable import's apply — would otherwise report success for a save that never happened. The form
	 * `onSubmit` ignores the value and is unaffected.
	 */
	const submitDeptConfig = useCallback(
		async (e?: React.FormEvent<HTMLFormElement>): Promise<SubmitDeptConfigResult> => {
			e?.preventDefault();
			// Short-circuit if aggregated save-disabled reasons exist; the UI surfaces
			// them via the Save button tooltip, so no snackbar is needed here.
			if (saveDisabledReasons.length > 0) {
				return { saved: false };
			}
			if (!validateDepartmentLocations()) {
				return { saved: false };
			}

			try {
				// New rows are the only rows without the persisted integration id. Capture this before the
				// save replaces the form echo, so an ordinary edit can never open a summary dialog.
				const newlyAddedHrisIntegrationType = generalFormData.hrisIntegrations.find((integration) => integration.id === undefined)?.type;
				if (isNewDept) {
					trackEvent("Department configuration - create new department configuration", {
						generalFormData: generalFormData,
						newUsers: [],
						newDepartmentLocations: newDepartmentLocations,
					});
					const newDeptData = await createDepartment();

					setNewUsers([]);
					setNewDepartmentLocations(newDeptData.newDepartmentLocations);
					setGeneralFormDataState(getFormDataFromDept(newDeptData.config));
					setIsDirty(false);
					trackEvent("Department configuration - department configuration successfully created", {
						department_id: newDeptData.config.department_id,
						generalFormData: generalFormData,
						newUsers: [],
						newDepartmentLocations: newDepartmentLocations,
					});
					showSnackbar(
						t("department.created.success", {
							departmentName: newDeptData.config.display_name,
						}),
						"success",
					);
					setChosenDepartment(newDeptData.config);
					await updateUserInfo();
					// Deliberately no webhook prompt on this path. The access token carries the department
					// list it was minted with, so the just-created department isn't in it and the subscribe
					// call would 403 until the token is re-minted. The Nursa section's button covers it once
					// the operator lands on the new department.
					if (newlyAddedHrisIntegrationType) onHrisIntegrationSaved(newlyAddedHrisIntegrationType, newDeptData.config.department_id);
				} else {
					trackEvent("Department configuration - update department configuration", {
						department_id: chosenDepartment.department_id,
						generalFormData: generalFormData,
						newDepartmentLocations: newDepartmentLocations,
					});
					showSnackbar(t("saving.your.changes"), "info");
					// Captured before the save: it decides whether this save is what moved the staff-visibility
					// horizon, and therefore whether it may overwrite the value the rest of the app is showing.
					const scheduleVisibilityChanged = !isSameScheduleVisibilityDate(
						generalFormData.staffScheduleDisplayUntilDate,
						chosenDepartment.staffScheduleDisplayUntilDate,
					);
					// Captured before the save: `chosenDepartment` is replaced by the refreshed row below, so
					// afterwards a newly-mapped Nursa facility is indistinguishable from one already mapped.
					const previousNursaFacilityId = chosenDepartment.nursa_facility_id?.trim() || null;
					const [newGeneralFormData, updatedDepartmentLocations] = await Promise.all([updateGeneralForm(), updateDepartmentLocations()]);

					// Decided here, immediately after the write that changed the id — not at the end of this
					// branch. Everything below can still bail (a rejected locations save, a missing userInfo,
					// a department missing from the refresh), and every one of those exits leaves the id
					// PERSISTED. Arming late meant those saves silently produced a mapped-but-unsubscribed
					// department that could never re-prompt, because the next save compares against the value
					// now stored. Support-gated to match the route, which 403s anyone else.
					const savedNursaFacilityId = generalFormData.nursaFacilityId?.trim() || null;
					const nursaFacilityChanged = savedNursaFacilityId !== previousNursaFacilityId;
					if (isSupportUser && nursaFacilityChanged) {
						if (savedNursaFacilityId) setPendingNursaWebhookFacilityId(savedNursaFacilityId);
						// The id this department just stopped using. Left on the registration it would keep
						// producing events that resolve to no department; the server refuses the removal if
						// another department still maps it.
						if (previousNursaFacilityId) void unregisterNursaFacilityWebhook(previousNursaFacilityId);
					}
					trackEvent("Department configuration - department configuration successfully updated", {
						department_id: chosenDepartment.department_id,
						generalFormData: newGeneralFormData,
						newDepartmentLocations: updatedDepartmentLocations,
					});
					setNewDepartmentLocations([]);
					setExistingDepartmentLocations((prev) =>
						prev.map((row) => (modifiedDepartmentLocations.has(row.id) ? updatedDepartmentLocations.find((updated) => updated.id === row.id) || row : row)),
					);
					setModifiedDepartmentLocations(new Set());
					// Only the entries this save actually flushed are spent. A toggle made while it was in
					// flight was never sent, and wiping the map wholesale dropped it next to a success
					// snackbar — with the dirty guard cleared, so leaving the page did not warn either.
					const remainingJobPpdOverrides = spendFlushedJobPpd(latestJobPpdOverrides.current);
					const transformedFormData = {
						...newGeneralFormData,
						emrIntegrations: generalFormData.emrIntegrations,
						// DAT-905: this save no longer writes the three wage rule tables, so whatever the echo carries
						// for them is not the stored state. Keep the form's own copy rather than adopt it.
						hoursRateConfig: generalFormData.hoursRateConfig,
						overtimeConfig: generalFormData.overtimeConfig,
						breakRulesConfig: generalFormData.breakRulesConfig,
						// The server echoes time columns as "HH:MM:SS" but the form stores float hours.
						// Without converting, `floatToTime` gets a string and the field shows "NaN:NaN"
						// until the page is reloaded.
						dayStart: toFloatHours(newGeneralFormData.dayStart) ?? undefined,
						payrollStartTime: toFloatHours(newGeneralFormData.payrollStartTime),
						// The config response isn't date-converted, so the echoed horizon would be a raw string
						// in a `DateTime` field. Keep the value the form just submitted.
						staffScheduleDisplayUntilDate: generalFormData.staffScheduleDisplayUntilDate,
						// DEFAULT: prefer the server echo; fall back to the just-saved form value so the
						// in-session round-trip is lossless even if the response omits the key.
						autoApproveRules: newGeneralFormData.autoApproveRules ?? generalFormData.autoApproveRules ?? [],
						// The server echoes an array; the form stores a job-id-keyed map. Spreading the
						// response would leave `jobShiftTimes[jobId]` undefined and blank every row until
						// a reload, so keep the map the form just submitted.
						jobShiftTimes: generalFormData.jobShiftTimes,
						// The save flushed these and refetched the persisted set, so that part of the buffer is
						// spent; anything left is an edit made after this save read the map.
						jobPpdOverrides: remainingJobPpdOverrides,
					};
					setGeneralFormDataState(transformedFormData);
					setIsDirty(Object.keys(remainingJobPpdOverrides).length > 0);
					setChosenDepartment((prev) =>
						// The hours-rate / overtime / break-rules tables and the training/orientation pair are all
						// owned by Team → Wage Settings and excluded from this endpoint's write, so the form's
						// page-load copies must not be echoed into shared state.
						withPreservedWageRulesConfig(
							{
								...prev,
								autoApproveRules: transformedFormData.autoApproveRules || [],
								department_wage_config: withPreservedTrainingOrientationWage(transformedFormData.departmentWageConfig, prev.department_wage_config),
							},
							prev,
						),
					);

					// Clear temporary state
					setNewDepartmentLocations([]);
					setModifiedDepartmentLocations(new Set());

					// Refresh department data from backend
					const userInfo = await updateUserInfo();
					await Promise.all([refreshDepartmentData(chosenDepartment.department_id, false, undefined, true), refreshScheduleCache({ allWeeks: true })]);
					if (!userInfo) {
						showErrorSnackbar("Failed to refresh user info after department update", {}, new Error("User info is undefined after update"));
						// The config is ALREADY PERSISTED by this point — this is post-commit refresh, not the
						// write. Reporting `saved: false` here tells the Airtable wizard a successful apply was
						// refused, so it emits `save_error`, keeps itself open and invites a second apply of
						// rows that already landed. The refresh failure is surfaced by the snackbar above.
						return { saved: true };
					}
					if (userInfo) {
						// Manually update chosenDepartment with fresh data from userInfo
						// This is necessary because ChosenDepartmentProvider only updates when department_id changes
						const relevantDepartment = userInfo.departments.find((dept) => dept.department_id === chosenDepartment.department_id);
						if (relevantDepartment) {
							// The config endpoint echoes inputs without persisted integration ids. Restore the
							// fresh department-shaped integrations so the next ordinary save is not mistaken
							// for another new integration.
							setGeneralFormDataState((prev) => ({
								...prev,
								hrisIntegrations: getFormDataFromDept(relevantDepartment).hrisIntegrations,
							}));
							showSnackbar(
								t("department.updated.success", {
									departmentName: newGeneralFormData.displayName,
								}),
								"success",
							);
							setChosenDepartment((prevState) => ({
								...relevantDepartment,
								// Only this save may move the horizon; otherwise keep the live in-memory value,
								// which the Staffing popup / auto-post job may have advanced since page load.
								staffScheduleDisplayUntilDate: scheduleVisibilityChanged
									? (transformedFormData.staffScheduleDisplayUntilDate ?? undefined)
									: prevState.staffScheduleDisplayUntilDate,
							}));
						}
						if (!relevantDepartment) {
							showErrorSnackbar(
								"Failed to find updated department in user info after department update",
								{},
								new Error("Updated department not found in user info"),
							);
							return { saved: false };
						}
					}
					if (newlyAddedHrisIntegrationType) onHrisIntegrationSaved(newlyAddedHrisIntegrationType, chosenDepartment.department_id);
				}
				return { saved: true };
			} catch (err) {
				if (err instanceof AutoApproveRuleValidationError) {
					showErrorSnackbar(err.message, {}, err);
					return { saved: false };
				}
				// DAT-464: a blocked job removal gets a dialog listing what to clear, not a snackbar —
				// the payload is a list of employees and shift counts, unreadable as one line of text.
				const jobConflicts = parseJobRemovalConflict(err);
				if (jobConflicts) {
					setJobRemovalConflicts(jobConflicts);
					return { saved: false };
				}
				// A named refusal from the config endpoint (the timezone lock answers 409 with a
				// `UIVisibleError`) is surfaced by KEY, so a programmatic caller can render that sentence
				// instead of "the save failed".
				const uiErrorKey = uiVisibleErrorKey(err);
				if (err instanceof Error) {
					// Check if it's a validation error from transformHoursRateConfigsForBackend
					if (
						err.message.includes('Please choose "All roles" or select specific roles') ||
						err.message.includes("Select at least one role for:") ||
						err.message.includes("Invalid time ranges") ||
						err.message.includes("Rate type (Flat Rate or Percentage) must be selected")
					) {
						showErrorSnackbar(err.message, {}, err);
						return { saved: false, uiErrorKey };
					}
					// Before the axios-detail branch: this one's `message` is the only thing that tells the
					// operator the department itself saved, and `cause` is the override write's own error.
					if (err instanceof JobPpdFlushError) {
						showErrorSnackbar(err.message, {}, err.cause);
						// The department config itself was written; only the PPD override flush after it failed.
						return { saved: true };
					}
					const error = err.cause as AxiosError<{ detail: string }>;
					trackErrorEvent("Department configuration - error submit department configuration", e, {
						department_id: chosenDepartment.department_id,
						generalFormData,
						newDepartmentLocations,
						existingDepartmentLocations,
						modifiedDepartmentLocations,
					});
					showErrorSnackbar(error.response?.data?.detail || err.message || `Failed to update ${generalFormData.displayName}`, {}, error);
				}
				return { saved: false, uiErrorKey };
			}
		},
		[
			saveDisabledReasons,
			validateDepartmentLocations,
			updateGeneralForm,
			createDepartment,
			updateDepartmentLocations,
			showErrorSnackbar,
			isNewDept,
			showSnackbar,
			generalFormData,
			chosenDepartment.department_id,
			chosenDepartment.staffScheduleDisplayUntilDate,
			chosenDepartment.nursa_facility_id,
			isSupportUser,
			unregisterNursaFacilityWebhook,
			newDepartmentLocations,
			existingDepartmentLocations,
			modifiedDepartmentLocations,
			updateUserInfo,
			onHrisIntegrationSaved,
			setChosenDepartment,
			refreshDepartmentData,
			refreshScheduleCache,
			spendFlushedJobPpd,
			t,
		],
	);

	// Closes the prompt either way: the mutation reports its own outcome through a snackbar, and a
	// failed subscription must not trap the operator behind a dialog the department no longer needs.
	const confirmNursaWebhookSubscription = useCallback(async () => {
		try {
			await registerNursaFacilityWebhook();
		} catch {
			// Already surfaced by the mutation's onError snackbar. Swallowed here only so awaiting
			// `mutateAsync` from the dialog's onConfirm doesn't leave an unhandled rejection.
		} finally {
			setPendingNursaWebhookFacilityId(null);
		}
	}, [registerNursaFacilityWebhook]);

	const dismissNursaWebhookPrompt = useCallback(() => setPendingNursaWebhookFacilityId(null), []);

	// The Nursa section's button. Separate from the dialog path because it must work when no prompt is
	// pending — a facility that was saved but never subscribed never re-prompts, so without this the
	// only way back in would be to change the id to something wrong and then change it back.
	const registerNursaWebhookNow = useCallback(async () => {
		try {
			await registerNursaFacilityWebhook();
		} catch {
			// Surfaced by the mutation's onError snackbar; swallowed so the click doesn't reject.
		}
	}, [registerNursaFacilityWebhook]);

	return {
		isNewDept,
		setIsNewDept,
		isTimezoneLocked,
		isTimezoneLockUnknown,
		generalFormData,
		setGeneralFormData,
		/**
		 * The RAW state setter — patches the form WITHOUT marking it dirty.
		 *
		 * Only for values a server-side action already committed (the Clipboard setup wizard writes
		 * `clipboard_facility_id` itself). Marking the form dirty for a value the server already holds
		 * would make the next Save re-send it, and would make "discard changes" look like it undid a
		 * committed write. Everything the USER edits must go through `setGeneralFormData`.
		 */
		syncGeneralFormData: setGeneralFormDataState,
		isDirty,
		newUsers,
		setNewUsers,
		existingUsers,
		setExistingUsers,
		existingDepartmentLocations,
		setExistingDepartmentLocations,
		newDepartmentLocations,
		setNewDepartmentLocations,
		modifiedDepartmentLocations,
		setModifiedDepartmentLocations,
		submitDeptConfig,
		isUpdatingDeptConfig: isUpdatingUsers || isUpdatingGeneralForm || isCreatingDept || isUpdatingDepartmentLocations,
		saveDisabledReasons,
		jobRemovalConflicts,
		dismissJobRemovalConflicts: () => setJobRemovalConflicts(null),
		pendingNursaWebhookFacilityId,
		registerNursaWebhookNow,
		confirmNursaWebhookSubscription,
		dismissNursaWebhookPrompt,
		isRegisteringNursaWebhook,
		tabErrorCounts,
		availableAdminData,
		isAvailableAdminDataLoading,
	};
}

/** Axios errors carry the HTTP status; a thrown assertion or a network failure has none to report. */
const httpStatusOf = (error: unknown): number | undefined => (isAxiosError(error) ? error.response?.status : undefined);

/**
 * The one `PUT /departments/locations/{id}`: the row as saved and how many recurring shifts moved. Every caller (the
 * v1 editor's save and its out-of-config adoption, the Locations page's Save) owns its own events and messages.
 */
export const putDepartmentLocation = async (departmentId: string, body: UpdateSingleDepartmentLocationBody): Promise<UpdateSingleDepartmentLocationResult> =>
	(await backendService.updateSingleDepartmentLocation(departmentId, body)).data;

/**
 * The one `POST /departments/locations`: inserts `new`, updates `existing`, and answers with the rows it saved. Every
 * caller (the bulk update, the creates, the sibling-hours save, the v1 create) owns its own events, checks and messages.
 */
export const postDepartmentLocations = async (
	departmentId: string,
	body: { new: DepartmentLocation[]; existing: DepartmentLocation[] },
): Promise<DepartmentLocation[]> => (await backendService.updateDepartmentLocations(departmentId, body)).data;

/** What a write did to the department's rows: saved over their old versions, created (appended), or removed. */
type LocationsChange =
	| { kind: "saved"; rows: DepartmentLocation[] }
	| { kind: "created"; rows: DepartmentLocation[] }
	| { kind: "removed"; ids: ReadonlySet<string> };

const changedLocations = (locations: DepartmentLocation[], change: LocationsChange): DepartmentLocation[] => {
	switch (change.kind) {
		case "saved": {
			const byId = new Map(change.rows.map((row) => [row.id, row]));
			// A row this save didn't touch stays as it is.
			return locations.map((location) => byId.get(location.id) ?? location);
		}
		case "created": {
			// The save returns only the rows it created: appended, and a row already listed is replaced, never listed twice.
			const createdIds = new Set(change.rows.map((row) => row.id));
			return [...locations.filter((location) => !createdIds.has(location.id)), ...change.rows];
		}
		case "removed":
			return locations.filter((location) => !change.ids.has(location.id));
	}
};

/**
 * The one merge of a write into the chosen department's `locations`, made only when the write's department is still the
 * chosen one: the admin can switch departments while it runs, and its rows belong to the department they were sent for.
 */
const mergeIntoDepartment = (setChosenDepartment: Dispatch<SetStateAction<Department>>, departmentId: string, change: LocationsChange): void => {
	setChosenDepartment((prev) => (prev.department_id !== departmentId ? prev : { ...prev, locations: changedLocations(prev.locations, change) }));
};

/**
 * The error a write hook's snackbar reports, or null for a refusal its caller shows in place (`refusalOf` reads it,
 * non-null). A refusal whose details break the contract is not one the caller can show, so the snackbar reports the
 * parse error instead.
 */
const snackbarErrorOf = (error: Error, refusalOf: (error: unknown) => unknown): unknown => {
	try {
		return refusalOf(error) === null ? error : null;
	} catch (contractError) {
		return contractError;
	}
};

/**
 * A mutation whose `mutate` and `mutateAsync` first turn the caller's variables into what is sent. `prepare` throws on
 * a caller bug (an empty batch, rows of another department), so `mutate` throws and `mutateAsync` rejects before any
 * request, event or snackbar. Stable while `mutation`'s functions and `prepare` are.
 */
const useGuardedMutation = <TData, TVariables extends object, TSent extends TVariables>(
	mutation: UseMutationResult<TData, Error, TSent>,
	prepare: (variables: TVariables) => TSent,
): UseMutationResult<TData, Error, TVariables> => {
	const { mutate, mutateAsync } = mutation;
	// TanStack types `mutate`'s arguments as a tuple conditional on whether the variables may be undefined, which TS
	// can't resolve for a type parameter. Every variables type here is an object (`TVariables extends object`), so the
	// tuple is always the required-variables one; and options typed for `TVariables` take `TSent`, its subtype.
	type SentArgs = MutateFunctionRest<TData, Error, TSent>;
	const guardedMutate = useCallback(
		(variables: TVariables, options?: MutateOptions<TData, Error, TVariables>) => mutate(...([prepare(variables), options] as SentArgs)),
		[mutate, prepare],
	);
	const guardedMutateAsync = useCallback(
		async (variables: TVariables, options?: MutateOptions<TData, Error, TVariables>) => mutateAsync(...([prepare(variables), options] as SentArgs)),
		[mutateAsync, prepare],
	);
	// Same reason: the guarded functions are the required-variables form of `mutate`/`mutateAsync` for `TVariables`,
	// and the rest of the result (its `variables` among it) holds a `TSent`, which is a `TVariables`.
	return { ...mutation, mutate: guardedMutate, mutateAsync: guardedMutateAsync } as UseMutationResult<TData, Error, TVariables>;
};

export function useUpdateDepartmentLocations({
	newDepartmentLocations,
	existingDepartmentLocations,
}: {
	newDepartmentLocations: DepartmentLocation[];
	existingDepartmentLocations: DepartmentLocation[];
}) {
	const {
		chosenDepartment: { department_id },
	} = useHospitalData();

	return useMutation<DepartmentLocation[], Error>({
		mutationFn: async () => {
			try {
				trackEvent("Department location - update department location record", {
					department_id,
					new_department_locations: newDepartmentLocations,
					existing_department_locations: existingDepartmentLocations,
				});

				// A 4xx/5xx answer rejects (axios), so it reaches the catch below.
				return await postDepartmentLocations(department_id, { new: newDepartmentLocations, existing: existingDepartmentLocations });
			} catch (e) {
				trackErrorEvent("Department location - error updating department location record", e, {
					department_id,
					new_department_locations: newDepartmentLocations,
					existing_department_locations: existingDepartmentLocations,
				});
				throw Error("Could not update department locations", { cause: e });
			}
		},
		throwOnError: false,
	});
}
export function useUpdateSingleDepartmentLocation() {
	const {
		chosenDepartment: { department_id },
		showErrorSnackbar,
	} = useHospitalData();
	const { t } = useTranslation();

	return useMutation<UpdateSingleDepartmentLocationResult, Error, UpdateSingleDepartmentLocationBody>({
		mutationFn: async (requestBody: UpdateSingleDepartmentLocationBody) => {
			trackEvent("updateSingleDepartmentLocation", {
				department_id,
				requestBody,
			});
			return putDepartmentLocation(department_id, requestBody);
		},
		throwOnError: false,
		onError: (error, variables) => {
			trackErrorEvent("useUpdateSingleDepartmentLocation_error", error, {
				variables,
			});
			showErrorSnackbar(t("location.config.update.error"), { department_id, variables }, error);
		},
	});
}

/**
 * Group-level rename of every `shift_template` row sharing a location name in the current department.
 * The READ side lives entirely on the chosen-department context, so callers own refreshing that context
 * (e.g. `performGlobalRefresh`) after a success — this hook only performs the write.
 *
 * No error snackbar: the rename popover (`pages/Locations/rename/`) shows a failure in place, beside the name it
 * refused (a duplicate's reason is long and needs the form next to it), and stays open to try again.
 */
export function useRenameDepartmentLocation() {
	const {
		chosenDepartment: { department_id },
	} = useHospitalData();

	return useMutation<RenameDepartmentLocationResult, Error, RenameDepartmentLocationBody>({
		mutationFn: async (body: RenameDepartmentLocationBody) => {
			const result = await backendService.renameDepartmentLocation(department_id, body);
			return result.data;
		},
		throwOnError: false,
	});
}

/**
 * Set the EMR link (unit/floor/rooms) for every `shift_template` row under one location name. The
 * READ side lives on the chosen-department context, so callers own updating that context after a
 * success — this hook only performs the write and surfaces failures.
 */
export function useSetDepartmentLocationEmr() {
	const {
		chosenDepartment: { department_id },
		showErrorSnackbar,
	} = useHospitalData();
	const { t } = useTranslation();

	return useMutation<SetLocationEmrResult, Error, SetLocationEmrBody>({
		mutationFn: async (body: SetLocationEmrBody) => {
			const result = await backendService.setDepartmentLocationEmr(department_id, body);
			return result.data;
		},
		throwOnError: false,
		onError: (error, variables) => {
			showErrorSnackbar(t("locations.emrEdit.error"), { department_id, variables }, error);
		},
	});
}

type FindDuplicateLocationRows = UseMutationResult<FindDuplicateLocationsResult, Error, FindDuplicateLocationsBody>;

/**
 * A batch with no rows is a caller bug, not a failed check: nothing is about to be saved, and the route
 * would refuse it (422). Thrown at the call, so no request, analytics event or "try again" snackbar
 * follows, which would tell the user to retry something that can never succeed.
 */
const assertHasCandidates = (body: FindDuplicateLocationsBody): FindDuplicateLocationsBody => {
	if (body.candidates.length === 0) {
		throw new Error("useFindDuplicateLocationRows: called with no candidates; call it only for a save that writes rows");
	}
	return body;
};

/**
 * Ask the backend which of `candidates` a save would refuse as duplicates (`POST
 * /departments/locations/duplicates`), before saving. Report-only: nothing is written, and an empty
 * `duplicates` means the save would be accepted. The backend owns the rule; every save endpoint
 * enforces it anyway, so this only spares a round trip that would end in a 409.
 *
 * `mutate` throws and `mutateAsync` rejects, before anything is sent, when `candidates` is empty.
 */
export function useFindDuplicateLocationRows(): FindDuplicateLocationRows {
	const {
		chosenDepartment: { department_id },
		showErrorSnackbar,
	} = useHospitalData();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	const mutation = useMutation<FindDuplicateLocationsResult, Error, FindDuplicateLocationsBody>({
		mutationFn: async ({ candidates }: FindDuplicateLocationsBody) => {
			track("locations_duplicate_check_start", { department_id, candidate_count: candidates.length });
			const result = await backendService.findDuplicateLocationRows(department_id, candidates);
			return result.data;
		},
		throwOnError: false,
		onSuccess: (result, { candidates }) => {
			track("locations_duplicate_check_success", { department_id, candidate_count: candidates.length, match_count: result.duplicates.length });
		},
		onError: (error, { candidates }) => {
			trackError("locations_duplicate_check_error", { department_id, candidate_count: candidates.length, error_message: error.message });
			showErrorSnackbar(t("locations.duplicates.checkError"), { department_id, candidate_count: candidates.length }, error);
		},
	});
	return useGuardedMutation(mutation, assertHasCandidates);
}

type CreateDepartmentLocationRows = UseMutationResult<DepartmentLocation[], Error, DepartmentLocation[]>;

/** What a failed create says, by what it was making: a new location, one shift, or copies of a shift. */
export type CreateLocationRowsErrorKey = "locations.new.create.error" | "locations.shift.create.error" | "locations.duplicateTo.create.error";

/**
 * An empty create, or one mixing departments, is a caller bug, not a failed save: thrown at the call, before any
 * request, event or snackbar.
 */
const assertHasRowsToCreate = (rows: DepartmentLocation[]): DepartmentLocation[] => {
	if (rows.length === 0) {
		throw new Error("useCreateDepartmentLocationRows: called with no rows; call it only when there is something to create");
	}
	departmentOfRows(rows);
	return rows;
};

/**
 * The save answered, but not with one row per row sent. It had already committed by then, so some rows may
 * exist; the page can't tell which, so it neither adds any nor lets the caller say that nothing was saved.
 */
export class CreatedRowsUnconfirmedError extends Error {
	constructor(sent: number, returned: number) {
		super(`useCreateDepartmentLocationRows: sent ${sent} rows but the save returned ${returned}`);
		this.name = "CreatedRowsUnconfirmedError";
	}
}

/**
 * The save echoes one row per created row. Any other count means the page can't tell which rows exist,
 * so it fails loud rather than showing a partial list.
 */
const assertCreatedEveryRow = (sent: DepartmentLocation[], created: DepartmentLocation[]): void => {
	if (created.length !== sent.length) {
		throw new CreatedRowsUnconfirmedError(sent.length, created.length);
	}
};

/**
 * The one department a create's rows belong to. The rows carry it, and they, not the chosen department at the time a
 * callback runs, say where the save went: the admin can switch departments while it runs, and TanStack hands the
 * pending mutation each re-render's options, so a department read from the hook's closure can be the new one.
 */
const departmentOfRows = (rows: DepartmentLocation[]): string => {
	const [first, ...rest] = rows;
	if (first === undefined) throw new Error("useCreateDepartmentLocationRows: no rows to read a department from");
	const other = rest.find((row) => row.department_id !== first.department_id);
	if (other !== undefined) {
		throw new Error(`useCreateDepartmentLocationRows: rows for ${first.department_id} and ${other.department_id} in one create`);
	}
	return first.department_id;
};

/**
 * Create new location rows (`POST /departments/locations` with `existing: []`) and add them to the
 * department's `locations` when it is still the chosen one. The save returns only the rows it created,
 * so they are appended to the list (a row whose id is already listed is replaced, never listed twice).
 * The request, its events and the list it lands in are the rows' own department (`departmentOfRows`).
 *
 * `mutate` throws and `mutateAsync` rejects, before anything is sent, when `rows` is empty or spans
 * departments. Two rejections come without a snackbar, for the caller to show:
 * - a 409 duplicate refusal: read it with `duplicateOfIdsFromError` and show the blocked notice;
 * - `CreatedRowsUnconfirmedError`: the save committed but its answer can't be matched to what was sent.
 *   The caller says so where the admin is looking (the popup's note), not in a second, passing message.
 * Any other failure shows `errorKey`, the caller's own words for what didn't get made (a location, a shift, copies).
 */
export function useCreateDepartmentLocationRows({ errorKey }: { errorKey: CreateLocationRowsErrorKey }): CreateDepartmentLocationRows {
	const { showErrorSnackbar, setChosenDepartment } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	const mutation = useMutation<DepartmentLocation[], Error, DepartmentLocation[]>({
		mutationFn: async (rows: DepartmentLocation[]) => {
			const department_id = departmentOfRows(rows);
			track("locations_rows_create_start", { department_id, row_count: rows.length });
			const created = await postDepartmentLocations(department_id, { new: rows, existing: [] });
			assertCreatedEveryRow(rows, created);
			return created;
		},
		throwOnError: false,
		onSuccess: (created, rows) => {
			const department_id = departmentOfRows(rows);
			mergeIntoDepartment(setChosenDepartment, department_id, { kind: "created", rows: created });
			track("locations_rows_create_success", { department_id, row_count: rows.length });
		},
		onError: (error, rows) => {
			const snackbarContext = { department_id: departmentOfRows(rows), row_count: rows.length };
			trackError("locations_rows_create_error", { ...snackbarContext, status: httpStatusOf(error), error_message: error.message });
			// The caller shows these two (see above); reported here all the same.
			if (error instanceof CreatedRowsUnconfirmedError) return;
			// The caller shows a duplicate refusal as the blocked notice.
			const shownError = snackbarErrorOf(error, duplicateOfIdsFromError);
			if (shownError === null) return;
			// Any other failure may or may not have reached the save (a timeout), so the message names no cause.
			showErrorSnackbar(t(errorKey), snackbarContext, shownError);
		},
	});
	return useGuardedMutation(mutation, assertHasRowsToCreate);
}

/**
 * Which Locations surface saves the row: the expanded editor's Save, or a change made right in the row (with the
 * control it came from). It picks the events (`locations_row_expand_save_*` / `locations_inline_edit_*`) and the error
 * snackbar's text. An inline change also says whether it was dropped since it was sent (`discarded`: its row is being
 * deleted), when its failure is the delete's doing and shows no snackbar.
 */
export type SaveShiftRowSurface = { kind: "expanded" } | { kind: "inline"; field: InlineEditField; discarded: () => boolean };

/** What a Locations row save sends (`useSaveShiftRow`): the expanded editor's Save, or an inline row change. */
export interface SaveShiftRowVariables {
	/** The edited row as it will be stored (`buildEditedRow`), sent as one PUT. */
	edited: DepartmentLocation;
	/** "yyyy-MM-dd": the first day the edit reaches the row's recurring shifts. */
	applyFromDate: string;
	/** The rows sharing the edited row's staff, already on its new hours (`withSharedHours`); [] when none. */
	siblings: DepartmentLocation[];
	/** Whether the Save ran the duplicate pre-check first, for the events. */
	ruleFieldsChanged: boolean;
	surface: SaveShiftRowSurface;
}

/** Re-send only the siblings after `SiblingHoursNotSavedError`: the edited row is already saved. Take it from the error's `retry`. */
export interface SiblingHoursRetry {
	kind: "siblingsRetry";
	/** The department the first save went to, which the retry goes to too. */
	departmentId: string;
	editedId: string;
	/** The first save's moved shifts, reported again so the retry's result reads the same as a whole save. */
	amountOfUpdatedShifts: number;
	siblings: DepartmentLocation[];
}

export interface SaveShiftRowResult {
	/** Recurring shifts the PUT moved; above 0, the caller refreshes the schedule. */
	amountOfUpdatedShifts: number;
	/** The edited row as the PUT answered, which is what the page now holds; null for a siblings retry (it saved before). */
	savedRow: DepartmentLocation | null;
	/** The edited row and every sibling saved, for the flash. */
	changedIds: string[];
}

/**
 * The edited row saved, but its siblings' new hours didn't. Their staff already moved with the edited row, so the
 * siblings are short until the retry lands. `retry` re-sends only them (`useSaveShiftRow().mutate(error.retry)`),
 * which is safe to repeat: it updates rows that already exist.
 */
export class SiblingHoursNotSavedError extends Error {
	readonly retry: SiblingHoursRetry;

	constructor(retry: SiblingHoursRetry, cause: unknown) {
		super(`useSaveShiftRow: the row saved but ${retry.siblings.length} rows sharing its staff did not`, { cause });
		this.name = "SiblingHoursNotSavedError";
		this.retry = retry;
	}
}

/**
 * The save was refused before anything was sent: its rows broke an invariant (another department, a repeated sibling).
 * No request, no event and no snackbar came of it, so the caller reports it (a bug, not a failed save).
 */
export class ShiftRowSaveNotSentError extends Error {
	constructor(cause: unknown) {
		super(`useSaveShiftRow: nothing was sent: ${errorText(cause)}`, { cause });
		this.name = "ShiftRowSaveNotSentError";
	}
}

/** A save as sent: the caller's variables plus the edited row's department, which the save goes to. */
interface SentRowSave extends SaveShiftRowVariables {
	kind: "row";
	departmentId: string;
}

type SentShiftRowSave = SentRowSave | SiblingHoursRetry;

type SaveShiftRow = UseMutationResult<SaveShiftRowResult, Error, SaveShiftRowVariables | SiblingHoursRetry>;

type SaveDeps = {
	track: ReturnType<typeof useAnalytics>["track"];
	trackError: ReturnType<typeof useAnalytics>["trackError"];
	setChosenDepartment: Dispatch<SetStateAction<Department>>;
};

const isSiblingsRetry = (variables: SaveShiftRowVariables | SiblingHoursRetry): variables is SiblingHoursRetry =>
	"kind" in variables && variables.kind === "siblingsRetry";

/** Rows for another department, a sibling that is the edited row, or a sibling named twice: caller bugs, thrown before anything is sent. */
const assertSaveableSiblings = (siblings: DepartmentLocation[], departmentId: string, editedId: string): void => {
	const ids = siblings.map((row) => row.id);
	if (new Set(ids).size !== ids.length) throw new Error("useSaveShiftRow: a sibling appears more than once");
	if (ids.includes(editedId)) throw new Error("useSaveShiftRow: the edited row can't be its own sibling");
	const foreign = siblings.find((row) => row.department_id !== departmentId);
	if (foreign !== undefined) throw new Error(`useSaveShiftRow: sibling ${foreign.id} belongs to department ${foreign.department_id}, not ${departmentId}`);
};

const assertSaveable = (variables: SaveShiftRowVariables | SiblingHoursRetry): void => {
	if (isSiblingsRetry(variables)) {
		if (variables.siblings.length === 0) throw new Error("useSaveShiftRow: a siblings retry needs at least one sibling");
		assertSaveableSiblings(variables.siblings, variables.departmentId, variables.editedId);
		return;
	}
	if (variables.edited.department_id === "") throw new Error(`useSaveShiftRow: row ${variables.edited.id} has no department`);
	assertSaveableSiblings(variables.siblings, variables.edited.department_id, variables.edited.id);
};

/**
 * The save as sent, to the edited row's own department: an inline change still waiting when the admin switches
 * departments is sent after the switch, and belongs to the department its row is in. Throws `ShiftRowSaveNotSentError`
 * on rows that break an invariant (`assertSaveable`).
 */
const stampShiftRowSave = (variables: SaveShiftRowVariables | SiblingHoursRetry): SentShiftRowSave => {
	try {
		assertSaveable(variables);
	} catch (error) {
		throw new ShiftRowSaveNotSentError(error);
	}
	return isSiblingsRetry(variables) ? variables : { ...variables, kind: "row", departmentId: variables.edited.department_id };
};

type RowSaveEvents = { start: string; success: string; error: string };

const ROW_SAVE_EVENTS: Record<SaveShiftRowSurface["kind"], RowSaveEvents> = {
	expanded: { start: "locations_row_expand_save_start", success: "locations_row_expand_save_success", error: "locations_row_expand_save_error" },
	inline: { start: "locations_inline_edit_start", success: "locations_inline_edit_success", error: "locations_inline_edit_error" },
};

const ROW_SAVE_ERROR_KEY: Record<SaveShiftRowSurface["kind"], string> = {
	expanded: "locations.expanded.save.error",
	inline: "locations.row.save.error",
};

/**
 * A row save's events and its own props (the inline field). A siblings retry, from the expanded editor or an inline
 * change, keeps the expanded editor's: its row saved before, and only its shared-hours events are its own.
 */
const rowSaveSurfaceOf = (sent: SentShiftRowSave): { events: RowSaveEvents; errorKey: string; props: AnalyticValues } => {
	const surface: SaveShiftRowSurface = sent.kind === "row" ? sent.surface : { kind: "expanded" };
	const props = surface.kind === "inline" ? { field: surface.field } : {};
	return { events: ROW_SAVE_EVENTS[surface.kind], errorKey: ROW_SAVE_ERROR_KEY[surface.kind], props };
};

/** The PUT answers with the row it saved; another row means the page can't tell what was stored. */
const saveEditedRow = async (sent: SentRowSave, deps: SaveDeps): Promise<UpdateSingleDepartmentLocationResult> => {
	const { departmentId, edited, applyFromDate } = sent;
	// The PUT core, not useUpdateSingleDepartmentLocation: that hook's own snackbar would stack on this one's.
	const data = await putDepartmentLocation(departmentId, { updatedLocation: edited, applyFromDate });
	if (data.updatedLocation.id !== edited.id) {
		throw new Error(`useSaveShiftRow: saved row ${edited.id} but the save answered with ${data.updatedLocation.id}`);
	}
	mergeIntoDepartment(deps.setChosenDepartment, departmentId, { kind: "saved", rows: [data.updatedLocation] });
	const { events, props } = rowSaveSurfaceOf(sent);
	deps.track(events.success, {
		department_id: departmentId,
		...props,
		sibling_count: sent.siblings.length,
		rule_fields_changed: sent.ruleFieldsChanged,
		amount_of_updated_shifts: data.amountOfUpdatedShifts,
	});
	return data;
};

/**
 * One config-only update of the siblings (`existing` only: their staff already moved with the PUT). Any failure,
 * an answer that doesn't list every sibling included, becomes `SiblingHoursNotSavedError` carrying the retry.
 */
const saveSiblingHours = async (retry: SiblingHoursRetry, deps: SaveDeps): Promise<void> => {
	const { departmentId, siblings } = retry;
	const eventProps = { department_id: departmentId, sibling_count: siblings.length };
	deps.track("locations_shared_hours_save_start", eventProps);
	try {
		const saved = await postDepartmentLocations(departmentId, { new: [], existing: siblings });
		const savedIds = new Set(saved.map((row) => row.id));
		if (saved.length !== siblings.length || siblings.some((row) => !savedIds.has(row.id))) {
			throw new Error(`useSaveShiftRow: sent ${siblings.length} siblings but the save answered with ${saved.length} other rows`);
		}
		mergeIntoDepartment(deps.setChosenDepartment, departmentId, { kind: "saved", rows: saved });
		deps.track("locations_shared_hours_save_success", eventProps);
	} catch (error) {
		deps.trackError("locations_shared_hours_save_error", {
			...eventProps,
			status: httpStatusOf(error),
			error_message: errorText(error),
		});
		throw new SiblingHoursNotSavedError(retry, error);
	}
};

const runShiftRowSave = async (sent: SentShiftRowSave, deps: SaveDeps): Promise<SaveShiftRowResult> => {
	if (sent.kind === "siblingsRetry") {
		deps.track("locations_shared_hours_retry", { department_id: sent.departmentId, sibling_count: sent.siblings.length });
		await saveSiblingHours(sent, deps);
		return {
			amountOfUpdatedShifts: sent.amountOfUpdatedShifts,
			savedRow: null,
			changedIds: [sent.editedId, ...sent.siblings.map((row) => row.id)],
		};
	}
	const { events, props } = rowSaveSurfaceOf(sent);
	deps.track(events.start, {
		department_id: sent.departmentId,
		...props,
		sibling_count: sent.siblings.length,
		rule_fields_changed: sent.ruleFieldsChanged,
	});
	const { updatedLocation: savedRow, amountOfUpdatedShifts } = await saveEditedRow(sent, deps);
	const changedIds = [sent.edited.id, ...sent.siblings.map((row) => row.id)];
	if (sent.siblings.length === 0) return { amountOfUpdatedShifts, savedRow, changedIds };
	await saveSiblingHours(
		{ kind: "siblingsRetry", departmentId: sent.departmentId, editedId: sent.edited.id, amountOfUpdatedShifts, siblings: sent.siblings },
		deps,
	);
	return { amountOfUpdatedShifts, savedRow, changedIds };
};

/**
 * A Locations row save, from the expanded editor's Save or an inline row change (`surface`):
 * `PUT /departments/locations/{id}`, then, when rows share its staff, one config-only `POST /departments/locations`
 * with those rows on the new hours. It goes to the edited row's own department (siblings must be that department's
 * too), and each saved row replaces its old version in the department's `locations` when that department is still the
 * chosen one. `mutate(error.retry)` re-sends only the siblings after a sibling failure.
 *
 * `mutate` throws and `mutateAsync` rejects with `ShiftRowSaveNotSentError`, before anything is sent, on a row with no
 * department, a sibling of another department or a repeated sibling; that one has no snackbar either, for the caller to
 * report. Two more rejections come without a snackbar, for the caller to show:
 * - a 409 duplicate refusal of the PUT: read it with `duplicateOfIdsFromError` and show the blocked notice;
 * - `SiblingHoursNotSavedError`: the row saved, its siblings didn't; offer its `retry`.
 * Anything else shows the surface's error snackbar (`locations.expanded.save.error` / `locations.row.save.error`). The
 * success message is the caller's.
 */
export function useSaveShiftRow(): SaveShiftRow {
	const { showErrorSnackbar, setChosenDepartment } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	const mutation = useMutation<SaveShiftRowResult, Error, SentShiftRowSave>({
		mutationFn: (sent: SentShiftRowSave) => runShiftRowSave(sent, { track, trackError, setChosenDepartment }),
		throwOnError: false,
		onError: (error, sent) => {
			// Tracked where it was thrown; the caller offers the retry.
			if (error instanceof SiblingHoursNotSavedError) return;
			const { events, errorKey, props } = rowSaveSurfaceOf(sent);
			const snackbarContext = { department_id: sent.departmentId, step: sent.kind, ...props };
			trackError(events.error, { ...snackbarContext, status: httpStatusOf(error), error_message: error.message });
			// An inline change whose row was deleted meanwhile: the row is gone, so there is nothing to say about it.
			if (sent.kind === "row" && sent.surface.kind === "inline" && sent.surface.discarded()) return;
			// The caller shows a duplicate refusal as the blocked notice.
			const shownError = snackbarErrorOf(error, duplicateOfIdsFromError);
			if (shownError === null) return;
			showErrorSnackbar(t(errorKey), snackbarContext, shownError);
		},
	});
	return useGuardedMutation(mutation, stampShiftRowSave);
}

/** A template-row delete, plus what the delete dialog knew when the admin confirmed it (sent with the events). */
export interface DeleteDepartmentLocationRowsVariables {
	ids: string[];
	/** "shift" removes one row; "location" removes every row of one location. */
	kind: "shift" | "location";
	/** Staff scheduled on these rows only; null when that count couldn't be read (`staffCheck: "error"`). */
	staffCount: number | null;
	staffCheck: "ok" | "error";
}

/** A delete as sent: the caller's variables plus the department chosen when `mutate` was called. */
interface SentDelete extends DeleteDepartmentLocationRowsVariables {
	departmentId: string;
}

type DeleteDepartmentLocationRows = UseMutationResult<number, Error, DeleteDepartmentLocationRowsVariables>;

/**
 * An empty delete, or one naming a row twice, is a caller bug, not a failed delete: thrown at the call, before any
 * request, event or snackbar. The backend refuses both with a 422 too.
 */
const assertHasRowsToDelete = ({ ids }: DeleteDepartmentLocationRowsVariables): void => {
	if (ids.length === 0) {
		throw new Error("useDeleteDepartmentLocationRows: called with no ids; call it only when there is something to delete");
	}
	if (new Set(ids).size !== ids.length) {
		throw new Error("useDeleteDepartmentLocationRows: an id appears more than once in one delete");
	}
};

/**
 * The delete removes every row sent or none (it answers 404 when one is missing), so any other count breaks that
 * contract: the page can't tell which rows are gone, so it leaves its list alone and fails loud.
 */
const assertDeletedEveryRow = (ids: string[], deletedRows: number): void => {
	if (deletedRows !== ids.length) {
		throw new Error(`useDeleteDepartmentLocationRows: sent ${ids.length} ids but the delete removed ${deletedRows} rows`);
	}
};

const deleteEventProps = ({ departmentId, ids, kind, staffCount, staffCheck }: SentDelete) => ({
	department_id: departmentId,
	kind,
	row_count: ids.length,
	staff_count: staffCount,
	staff_check: staffCheck,
});

/**
 * Delete template rows of the chosen department (`POST /departments/locations/delete`), all or none, and remove
 * them from its `locations` when it is still the chosen one. The department is fixed at the `mutate` call and
 * travels with the variables: the admin can switch departments while the delete runs, and TanStack hands the
 * pending mutation each re-render's options, so a department read from the hook's closure in a callback (even in
 * `mutationFn`, which starts a few ticks after the call) can be the new one.
 *
 * `mutate` throws and `mutateAsync` rejects, before anything is sent, when `ids` is empty or repeats an id.
 * A 409 credentials refusal with its `details.rowIds` rejects without a snackbar, for the dialog to show;
 * every other failure, a removed-row count that doesn't match the ids sent included, shows the error snackbar.
 * The success message is the caller's, which knows what was removed.
 */
export function useDeleteDepartmentLocationRows(): DeleteDepartmentLocationRows {
	const {
		chosenDepartment: { department_id },
		showErrorSnackbar,
		setChosenDepartment,
	} = useHospitalData();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	const mutation = useMutation<number, Error, SentDelete>({
		mutationFn: async (sent: SentDelete) => {
			track("locations_delete_start", deleteEventProps(sent));
			const { data } = await backendService.deleteDepartmentLocationRows(sent.departmentId, sent.ids);
			assertDeletedEveryRow(sent.ids, data.deletedRows);
			return data.deletedRows;
		},
		throwOnError: false,
		onSuccess: (_deletedRows, sent) => {
			mergeIntoDepartment(setChosenDepartment, sent.departmentId, { kind: "removed", ids: new Set(sent.ids) });
			track("locations_delete_success", deleteEventProps(sent));
		},
		onError: (error, sent) => {
			trackError("locations_delete_error", { ...deleteEventProps(sent), status: httpStatusOf(error), error_message: error.message });
			const snackbarContext = { department_id: sent.departmentId, kind: sent.kind, row_count: sent.ids.length };
			// The delete dialog names the rows of a credentials refusal.
			const shownError = snackbarErrorOf(error, credentialRowIdsFromError);
			if (shownError === null) {
				track("locations_delete_blocked", { ...snackbarContext, reason: "credentials", source: "409" });
				return;
			}
			// Any other failure may or may not have reached the delete (a timeout), so the message names no cause;
			// a UIVisibleError (`locations.delete.mismatch`, say) shows its own text instead.
			showErrorSnackbar(t("locations.delete.error"), snackbarContext, shownError);
		},
	});

	const stamp = useCallback(
		(variables: DeleteDepartmentLocationRowsVariables): SentDelete => {
			assertHasRowsToDelete(variables);
			return { ...variables, departmentId: department_id };
		},
		[department_id],
	);
	return useGuardedMutation(mutation, stamp);
}

export function useDeleteDepartmentLocations({ id }: { id: string }) {
	const { t } = useTranslation();
	const {
		chosenDepartment: { department_id },
		setChosenDepartment,
		showErrorSnackbar,
	} = useHospitalData();
	const { showSnackbar } = useSnackbar();
	return useMutation<void, Error>({
		mutationFn: async () => {
			try {
				trackEvent("Department location - delete department location record", {
					department_id,
					location_id: id,
				});

				// A 4xx/5xx answer rejects (axios), so it reaches the catch below.
				await backendService.deleteDepartmentLocations(department_id, id);
				trackEvent("Department location - department location record successfully deleted", {
					department_id,
					location_id: id,
				});
				showSnackbar(t("successfully.deleted.department.location"), "success");
				mergeIntoDepartment(setChosenDepartment, department_id, { kind: "removed", ids: new Set([id]) });
			} catch (e) {
				trackErrorEvent("Department location - error deleting department location record", e, {
					department_id,
					location_id: id,
				});
				throw Error("Could not delete the department location", {
					cause: e,
				});
			}
		},
		onError: (error) => {
			// mutationFn wraps every failure with its `cause`, and the cause carries the backend's answer: a
			// UIVisibleError (`locations.delete.hasCredentials`, `locations.delete.mismatch`) shows its own text.
			showErrorSnackbar(t("locations.delete.error"), { department_id, location_id: id }, error.cause);
		},
	});
}

export function useGetPccRooms() {
	const { chosenDepartment } = useHospitalData();
	return useQuery({
		queryKey: ["pccRooms", chosenDepartment.department_id],
		queryFn: async () => {
			const response = await backendService.getPccRooms(chosenDepartment.department_id);
			return response.data;
		},
		enabled: !!chosenDepartment.department_id,
		refetchOnWindowFocus: false,
	});
}
